import { of, firstValueFrom } from "rxjs";
import { EventEmitter } from "../utils/EventEmitter";
import { Infra3DDataProvider as Infra3DDataProvider_v2 } from "../api/Infra3DDataProvider_v2";
import { Infra3DDataProvider as Infra3DDataProvider_v3 } from "../api/Infra3DDataProvider_v3";
import { ComponentController } from "./ComponentController";
import { Container } from "./Container";
import { Navigator } from "./Navigator";
import { Observer } from "./Observer";
import { UtilsBase as Utils } from "../api/Infra3DDataProviderBase";
import { JumpMode } from "../jump/JumpMode";
import { first, map, mergeMap, switchMap, take, timeout } from "rxjs/operators";
import { of as observableOf } from "rxjs";
import proj4 from "proj4";
import { setApmLogger } from "../../infra3d";
import { STREAMTYPE } from "../internal";
// TODO: Update API and then all docs.
// TODO: Remove dead code, the viewer folder was skipped.
/**
 * @class Viewer
 *
 * @classdesc
 *
 * Represents a viewer component responsible for displaying and interacting with content.
 *
 * The {@link Viewer} class is the core component of the application, responsible for rendering
 * and facilitating interaction with various content types, including 3D models, simulations, and visualizations.
 * You can create a Viewer by specifying a container, image key, and other options.
 * It offers methods and events for programmatic interaction, with asynchronous methods returning
 * promises for results and notifications emitted through JavaScript events.
 */
// @classdesc The Viewer object represents the navigable image viewer.
// Create a Viewer by specifying a container, client ID, image key and
// other options. The viewer exposes methods and events for programmatic
// interaction.
//
// In the case of asynchronous methods, MapillaryJS returns promises to
// the results. Notifications are always emitted through JavaScript events.
//
// The viewer works with a few different coordinate systems.
//
// Container pixel coordinates
//
// Pixel coordinates are coordinates on the viewer container. The origin is
// in the top left corner of the container. The axes are
// directed according to the following for a viewer container with a width
// of 640 pixels and height of 480 pixels.
//
// ```
// (0,0)                          (640, 0)
//      +------------------------>
//      |
//      |
//      |
//      v                        +
// (0, 480)                       (640, 480)
// ```
//
// Basic image coordinates
//
// Basic image coordinates represents points in the original image adjusted for
// orientation. They range from 0 to 1 on both axes. The origin is in the top left
// corner of the image and the axes are directed
// according to the following for all image types.
//
// ```
// (0,0)                          (1, 0)
//      +------------------------>
//      |
//      |
//      |
//      v                        +
// (0, 1)                         (1, 1)
// ```
//
// For every camera viewing direction it is possible to convert between these
// two coordinate systems for the current node. The image can be panned and
// zoomed independently of the size of the viewer container resulting in
// different conversion results for different viewing directions.
// FIXME
// TODO: All async public main APIs should be Promise based.
export class Viewer extends EventEmitter {
    /**
     * @ignore
     * Create a new viewer instance.
     *
     * @description It is possible to initialize the viewer with or
     * without a key.
     *
     * When you want to show a specific image in the viewer from
     * the start you should initialize it with a key.
     *
     * When you do not know the first image key at implementation
     * time, e.g. in a map-viewer application you should initialize
     * the viewer without a key and call `moveToKey` instead.
     *
     * When initializing with a key the viewer is bound to that key
     * until the node for that key has been successfully loaded.
     * Also, a cover with the image of the key will be shown.
     * If the data for that key can not be loaded because the key is
     * faulty or other errors occur it is not possible to navigate
     * to another key because the viewer is not navigable. The viewer
     * becomes navigable when the data for the key has been loaded and
     * the image is shown in the viewer. This way of initializing
     * the viewer is mostly for embedding in blog posts and similar
     * where one wants to show a specific image initially.
     *
     * If the viewer is initialized without a key (with null or
     * undefined) it is not bound to any particular key and it is
     * possible to move to any key with `viewer.moveToKey("<my-image-key>")`.
     * If the first move to a key fails it is possible to move to another
     * key. The viewer will show a black background until a move
     * succeeds. This way of intitializing is suited for a map-viewer
     * application when the initial key is not known at implementation
     * time.
     *
     * @param {IViewerOptions} options - Optional configuration object
     * specifing Viewer's and the components' initial setup.
     *
     * @example
     * ```
     * var viewer = new Mapillary.Viewer({
     *     apiClient: "<my-client-id>",
     *     container: "<my-container-id>",
     * });
     * ```
     */
    constructor(options) {
        super();
        // Function that processes the results of a measuring tool
        this._processMeasurementResult = (toolname, info) => {
            switch (toolname) {
                case "pointMeasure":
                    return {
                        shortResult: undefined, // No numeric result for this tool
                        geometry: JSON.parse(JSON.stringify(info.pointfeature.geometry)), // drawpointtool.ts, line 436
                    };
                    break;
                case "lengthMeasure":
                    return {
                        shortResult: info.distance, // drawlinestringtool.ts, line 60
                        geometry: JSON.parse(JSON.stringify(info.linestringfeature.geometry)), // drawlinestringtool.ts, line 844
                    };
                    break;
                case "areaMeasure":
                    return {
                        shortResult: info.area, // drawpolygontool.ts, line 650
                        geometry: JSON.parse(JSON.stringify(info.polygonfeature.geometry)), // drawpolygontool.ts, line 647
                    };
                    break;
                case "verticalDistance":
                    return {
                        shortResult: info.distance, // elevationtool.ts, line 448
                        geometry: JSON.parse(JSON.stringify(info.linestringfeature.geometry)), // elevationtool.ts, line 28
                    };
                    break;
                // case "surveyWheel":
                //     return {
                //         shortResult: info.axis, // surveywheelmeasuretool.ts, line 90
                //         geometry: JSON.parse(info.geoframelist.toGeojsonString()), // surveywheelmeasuretool.ts, line 82
                //     };
                //     break;
                case "orthogonalDistance":
                    return {
                        shortResult: info.distance, // orthogonalmeasurementtool.ts, line 88
                        geometry: JSON.parse(JSON.stringify(info.linestringfeature.geometry)), // orthogonalmeasurementtool.ts, line 927
                    };
                    break;
                case "profile":
                    return {
                        shortResult: info, // No numeric result for this tool
                        geometry: undefined, // profiletool.ts, line 54 --> DOES NOT WORK
                    };
                    break;
                default:
                    console.error("Unknown Measure Tool:", toolname);
                    return null;
                    break;
                /*
                // maybe this details are not needed yet -> could be an extension for "after MVP"
                "results": [
                    {
                        "value": info.distance,
                        "type" : "inclined distance",
                        "accuracy": info.??
                        "unit": "m"
                    },
                    {
                        "value": info.distanceHz,
                        "type" : "horizontal distance",
                        "accuracy": info.??
                        "unit": "m"
                    }
                ],
                "shortResult": info.distance,
                "geometry": JSON.parse(info.linestringfeature.toGeojsonString()) --> //make shure that this is not of type mmFeature but geojson. maybe transform it also to wgs84.. (geojson actually does not allow local CRS)
                };
                */
            }
        };
        // Function that processes the continuous draw results of a measuring tool
        this._processContinuousDraw = (toolname, continuousInfo) => {
            switch (toolname) {
                case "pointMeasure":
                    return {
                        shortResult: undefined, // No numeric result for this tool
                    };
                    break;
                case "lengthMeasure":
                    return {
                        shortResult: continuousInfo.distance,
                    };
                    break;
                case "areaMeasure":
                    return {
                        shortResult: continuousInfo.area,
                    };
                    break;
                case "verticalDistance":
                    return {
                        shortResult: continuousInfo.distance,
                    };
                    break;
                // case "surveyWheel":
                //     return {
                //         shortResult: continiousInfo.continiousdraw
                //     };
                //     break;
                // case "prependicularDistance":
                //     return {
                //          shortResult: continiousInfo.continiousdraw
                //     };
                //     break;
                case "profile":
                    return {
                        shortResult: continuousInfo, // No numeric result for this tool
                    };
                    break;
                default:
                    console.error("Unknown Measure Tool:", toolname);
                    return null;
            }
        };
        this.authObject = options.authObject;
        this._navigator = new Navigator(options);
        if (options.imageKey !== undefined) {
            this._navigator.moveToKey$(options.imageKey);
        }
        if (options.frameFilter !== undefined) {
            this.setFrameFilter(options.frameFilter);
        }
        this._container = new Container(options, this._navigator);
        // NOTE: Abstraktion von Streams zu Event-Emitter-Pattern
        this._observer = new Observer(this, this._navigator, this._container);
        this._observer.startEmit();
        this._componentController = new ComponentController(this._container, this._navigator, this._observer, options.imageKey, options.component);
        this._navigator.lookAtService.setScene(this._container.scene);
        this._navigator.jumpNodeCalculator.setScene(this._container.scene);
        this._container.scene.setViewer(this);
        this._epsg = options.epsg;
        if (this._epsg && !proj4.defs[`EPSG:${this._epsg}`]) {
            fetch(`https://epsg.io/${this._epsg}.proj4`)
                .then((response) => {
                response.text().then((projdef) => {
                    proj4.defs(`EPSG:${this._epsg}`, projdef);
                });
            })
                .catch((reason) => {
                console.error(`Could not load EPSG:${this._epsg}`);
            });
        }
        if (options.apm) {
            setApmLogger(options.apm);
        }
    }
    setCollection(collection) {
        if (this._navigator.api.data instanceof Infra3DDataProvider_v2 ||
            this._navigator.api.data instanceof Infra3DDataProvider_v3) {
            this._navigator.stateService.clearNodes();
            this._navigator.api.data.setCollection(collection);
        }
    }
    getCollection() {
        if (this._navigator.api.data instanceof Infra3DDataProvider_v2 ||
            this._navigator.api.data instanceof Infra3DDataProvider_v3) {
            return this._navigator.api.data.getCollection();
        }
        return null;
    }
    setJumpMode(mode) {
        if (typeof mode === "string")
            mode = JumpMode[mode];
        this._navigator.setJumpMode(mode);
    }
    // TODO: Actually would be better to abstract all of public intefaces
    // of scene via the viever facade.
    getScene() {
        return this._container.scene;
    }
    /**
     * @ignore
     * Returns the viewer's <canvas> element.
     *
     * @description This is the element onto which the viewer renders
     * the WebGL content.
     *
     * @returns {HTMLCanvasElement} The viewer's <canvas> element, or
     * null or not initialized.
     */
    getCanvas() {
        return this._container.canvas;
    }
    /**
     * @ignore
     * Returns the HTML element containing the viewer's <canvas> element.
     *
     * @description This is the element to which event bindings for viewer
     * interactivity (such as panning and zooming) are attached.
     *
     * @returns {HTMLDivElement} The container for the viewer's
     * <canvas> element.
     */
    getCanvasContainer() {
        return this._container.canvasContainer;
    }
    /**
     * @ignore
     * Returns the viewer's containing HTML element.
     *
     * @returns {HTMLElement} The viewer's container.
     */
    // FIXME
    getContainer() {
        return this._container.container;
    }
    /**
     * Return a boolean indicating if the viewer is in a navigable state.
     *
     * @description The navigable state indicates if the viewer supports
     * moving, i.e. calling the {@link moveToKey} and {@link moveDir}
     * methods or changing the authentication state,
     * i.e. calling {@link setUserToken}. The viewer will not be in a navigable
     * state if the cover is activated and the viewer has been supplied a key.
     * When the cover is deactivated or the viewer is activated without being
     * supplied a key it will be navigable.
     *
     * @returns {boolean} Boolean indicating whether the viewer is navigable.
     */
    get isNavigable() {
        return this._componentController.navigable;
    }
    /**
     * Navigate to a given image key.
     *
     * @param {string} key - A valid Mapillary image key.
     * @returns {Promise<Node>} Promise to the node that was navigated to.
     * @throws {Error} Propagates any IO errors to the caller.
     * @throws {Error} When viewer is not navigable.
     * @throws {@link AbortInfra3DSDKError} When a subsequent move request is made
     * before the move to key call has completed.
     *
     * @example
     * ```
     * viewer.moveToKey("<my key>").then(
     *     (n) => { console.log(n); },
     *     (e) => { console.error(e); });
     * ```
     */
    moveToKey(key, pointto) {
        if (!this.isNavigable) {
            return Promise.reject(new Error("Calling moveToKey is not supported when viewer is not navigable."));
        }
        const result$ = of(null).pipe(switchMap(() => {
            if (pointto) {
                // Directly returned, as it is waiting inside the function for the geoframe to be updated
                return this._navigator.lookAtService.moveToKeyAndPointTo$(key, pointto);
            }
            const currentKey = this.getScene().getCurrentGeoframe()?.key;
            // No move necessary, return the current node.
            if (key === currentKey) {
                return this._navigator.stateService.currentNode$.pipe(take(1));
            }
            return this._navigator.moveToKey$(key).pipe(switchMap((newNode) => {
                // Wait till the geoframe is also updated
                return this.getScene().userinputapi.geoframechanged$.pipe(take(1), map(() => newNode));
            }));
        }), take(1));
        return firstValueFrom(result$);
    }
    /**
     * Move to Camera
     *
     * Moves the viewer to a specific camera identified by its capturesystemID.
     *
     * @param {string} capturesystemID - The unique identifier of the camera to move to.
     * @returns {Promise<Node>} - A Promise that resolves with a Node representing the camera's new position.
     *
     * This method first checks if the viewer is navigable. If it is, it calls the {@link moveToCamera} method,
     * passing the capturesystemID as a parameter, to initiate the camera movement. If the viewer is not navigable,
     * it rejects the Promise with an error, indicating that moving to the camera is not supported.
     *
     * The method returns a Promise that resolves with a Node representing the new position of the camera after the move.
     * If successful, the Promise resolves with the Node; otherwise, it rejects with an error.
     */
    moveToCamera(capturesystemID) {
        if (!this.isNavigable) {
            return Promise.reject(new Error("Calling moveToCamera is not supported when viewer is not navigable."));
        }
        return firstValueFrom(this._navigator.moveToCamera$(capturesystemID));
    }
    /**
     * Move to Campaign
     *
     * Moves the viewer to a specific campaign identified by its campaignID.
     *
     * @param {string} campaignID - The unique identifier of the campaign to move to.
     * @returns {Promise<Node>} - A Promise that resolves with a Node representing the new position within the campaign.
     *
     * This method first checks if the viewer is navigable. If it is, it calls the {@link moveToCampaign} method,
     * passing the campaignID as a parameter, to initiate the navigation to the campaign. If the viewer is not navigable,
     * it rejects the Promise with an error, indicating that moving to the campaign is not supported.
     *
     * The method returns a Promise that resolves with a Node representing the new position within the campaign after the move.
     * If successful, the Promise resolves with the Node; otherwise, it rejects with an error.
     */
    moveToCampaign(campaignID) {
        if (!this.isNavigable) {
            return Promise.reject(new Error("Calling moveToCampaign is not supported when viewer is not navigable."));
        }
        return firstValueFrom(this._navigator.moveToCampaign$(campaignID));
    }
    /**
     * @ignore
     * Navigate in a given direction.
     *
     * @description This method has to be called through EdgeDirection enumeration as in the example.
     *
     * @param {EdgeDirection} direction - Direction in which which to move.
     * @returns {Promise<Node>} Promise to the node that was navigated to.
     * @throws {Error} If the current node does not have the edge direction
     * or the edges has not yet been cached.
     * @throws {Error} Propagates any IO errors to the caller.
     * @throws {Error} When viewer is not navigable.
     * @throws  {@link AbortInfra3DSDKError} When a subsequent move request is made
     * before the move dir call has completed.
     *
     * @example
     * ```
     * viewer.moveDir(mm.EdgeDirection.Next).then(
     *     (n) => { console.log(n); },
     *     (e) => { console.error(e); });
     * ```
     */
    moveDirection(direction) {
        if (!this.isNavigable) {
            return Promise.reject(new Error("Calling moveDirection is not supported when viewer is not navigable."));
        }
        return firstValueFrom(this._navigator.moveDir$(direction));
    }
    /**
     *
     * @param xyz
     * @param maxdist
     * @param epsg
     * @returns
     */
    movePositionLookAtPosition(xyz, maxdist, epsg) {
        //use this for debugging
        /*         const mapcomponent: MapComponent = this.getComponent("map");
        this._navigator.lookAtService.setMap(mapcomponent.map); */
        if (!this.isNavigable) {
            return Promise.reject(new Error("Calling movePosLookAt is not supported when viewer is not navigable."));
        }
        return firstValueFrom(this._navigator.lookAtService.movePosLookAt$(xyz, maxdist, epsg || this._epsg));
    }
    /**
     * Move Position and Look at Position
     *
     * Moves the viewer's position and adjusts the view to look at a specific geographical position defined by XYZ coordinates.
     *
     * @param {object} xyz - An object containing the XYZ coordinates of the target location:
     *   - easting: The easting (horizontal) coordinate.
     *   - northing: The northing (vertical) coordinate.
     *   - height (optional): The height or elevation (default is optional).
     * @param {number} maxdist - An optional maximum distance for the move operation (default is optional).
     * @param {number} epsg - An optional EPSG code specifying the coordinate system (default is optional).
     * @returns {Promise<[Node, GeoFrame]>} - A Promise that resolves with an array containing:
     *   - A Node representing the viewer's new position.
     *   - A GeoFrame indicating the frame of the new position.
     *
     * This method allows the viewer to move its position and adjust the view to look at a specific geographical location defined
     * by the provided XYZ coordinates. The optional 'maxdist' parameter can be used to limit the maximum distance of the move operation.
     * The 'epsg' parameter specifies the coordinate system, and if not provided, the viewer's default EPSG code is used.
     *
     * The method returns a Promise that resolves with an array containing the Node representing the viewer's new position and
     * a GeoFrame indicating the frame of the new position. If successful, the Promise resolves with these values; otherwise,
     * it rejects with an error.
     */
    moveToKeyAndLookAt(key, xyz, maxdist) {
        if (!this.isNavigable) {
            return Promise.reject(new Error("Calling movePosLookAt is not supported when viewer is not navigable."));
        }
        return firstValueFrom(this._navigator.lookAtService.moveToKeyAndLookAt$(key, xyz, maxdist));
    }
    /**
     * Move Position
     *
     * Moves the viewer's position to a specific geographical location defined by projected XYZ coordinates.
     *
     * @param {object} xyz - An object containing the XYZ coordinates of the target location:
     *   - easting: The easting (horizontal) coordinate.
     *   - northing: The northing (vertical) coordinate.
     *   - height (optional): The height or elevation (default is optional).
     * @param {number} epsg - An optional EPSG code specifying the coordinate system (default is optional).
     * @param {number} maxdist - An optional maximum distance for the move operation (default is optional).
     * @returns {Promise<Node>} - A Promise that resolves once the viewer's position has been successfully moved.
     *
     * This method allows the viewer to move its position to a specific geographical location defined by the provided XYZ coordinates.
     * The 'epsg' parameter specifies the coordinate system, and if not provided, the viewer's default EPSG code is used.
     * The 'maxdist' parameter can be used to limit the maximum distance of the move operation.
     *
     * The method returns a Promise that resolves once the viewer's position has been successfully moved. If the operation is successful,
     * the Promise resolves with the new node; otherwise, it rejects with an error.
     */
    movePosition(xyz, epsg, maxdist) {
        if (!this.isNavigable) {
            return Promise.reject(new Error("Calling movePosition is not supported when viewer is not navigable."));
        }
        const latlon = Utils.projectedFrom({ lon: xyz.easting, lat: xyz.northing }, epsg || this._epsg);
        const currentKey = this.getScene().getCurrentGeoframe()?.key;
        return firstValueFrom(this._navigator.movePosition$(latlon, maxdist).pipe(switchMap((newNode) => {
            // If no move is necessary, return the current node.
            if (newNode.key === currentKey) {
                return of(newNode);
            }
            // Wait till the geoframe is also updated
            return this.getScene().userinputapi.geoframechanged$.pipe(take(1), map(() => newNode));
        })));
    }
    /**
     * @ignore
     * Set the filter selecting nodes to use when calculating
     * the spatial edges.
     *
     * @description The following filter types are supported:
     *
     * Comparison
     *
     * `["==", key, value]` equality: `node[key] = value`
     *
     * `["!=", key, value]` inequality: `node[key] ≠ value`
     *
     * `["<", key, value]` less than: `node[key] < value`
     *
     * `["<=", key, value]` less than or equal: `node[key] ≤ value`
     *
     * `[">", key, value]` greater than: `node[key] > value`
     *
     * `[">=", key, value]` greater than or equal: `node[key] ≥ value`
     *
     * Set membership
     *
     * `["in", key, v0, ..., vn]` set inclusion: `node[key] ∈ {v0, ..., vn}`
     *
     * `["!in", key, v0, ..., vn]` set exclusion: `node[key] ∉ {v0, ..., vn}`
     *
     * Combining
     *
     * `["all", f0, ..., fn]` logical `AND`: `f0 ∧ ... ∧ fn`
     *
     * A key must be a string that identifies a property name of a
     * simple {@link Node} property. A value must be a string, number, or
     * boolean. Strictly-typed comparisons are used. The values
     * `f0, ..., fn` of the combining filter must be filter expressions.
     *
     * Clear the filter by setting it to null or empty array.
     *
     * Commonly used filter properties (see the {@link Node} class
     * documentation for a full list of properties that can be used
     * in a filter) and common use cases:
     *
     * ```
     * cameraType      // Show only spherical or not
     * organizationKey // Show images from one or several organizations
     * sequenceKey     // Show images from one or several sequences
     * userKey         // Show images from one or several users
     * capturedAt      // Show images from a certain time interval
     * ```
     *
     * @param {FilterExpression} filter - The filter expression.
     * @returns {Promise<void>} Promise that resolves after filter is applied.
     *
     * @example
     * ```
     * viewer.setFilter(["==", "sequenceKey", "<my sequence key>"]);
     *
     * // Other examples
     * // viewer.setFilter(["==", "organizationKey", "<my organization key>"]);
     * // viewer.setFilter(["in", "userKey", "<my user key #1>", "<my user key #2>"]);
     * // viewer.setFilter(["==", "cameraType", "cubemap"]);
     * // viewer.setFilter([">=", "capturedAt", <my time stamp>]);
     * ```
     */
    // FIXME: Add filter for sub-properties.
    setFilter(filter) {
        return new Promise((resolve, reject) => {
            this._navigator.setFilter$(filter).subscribe(() => {
                resolve(undefined);
            }, (error) => {
                reject(error);
            });
        });
    }
    /**
     * @ignore
     * Set a permanent filter on frame properties
     */
    setFrameFilter(frame_properties) {
        return new Promise((resolve, reject) => {
            this._navigator.setFrameFilter$(frame_properties).subscribe(() => {
                resolve(undefined);
            }, (error) => {
                reject(error);
            });
        });
    }
    /**
     * @ignore
     * Activate a component.
     *
     * @param {string} name - Name of the component which will become active.
     *
     * @example
     * ```
     * viewer.activateComponent("direction");
     * ```
     */
    // TODO: Document possibles names and type theme for TS.
    activateComponent(name) {
        this._componentController.activate(name);
    }
    /**
     * @ignore
     * Deactivate a component.
     *
     * @param {string} name - Name of component which become inactive.
     *
     * @example
     * ```
     * viewer.deactivateComponent("direction");
     * ```
     */
    // TODO: Document possibles names and type theme for TS.
    deactivateComponent(name) {
        this._componentController.deactivate(name);
    }
    /**
     * @ignore
     * Get a component.
     *
     * @param {string} name - Name of component.
     * @returns {Component} The requested component.
     *
     * @example
     * ```
     * var directionComponent = viewer.getComponent("direction");
     * ```
     */
    getComponent(name) {
        return this._componentController.get(name);
    }
    /**
     * Get the viewer's current vertical field of view.
     *
     * @description The vertical field of view rendered on the viewer canvas
     * measured in degrees.
     *
     * @returns {Promise<number>} Promise to the current field of view
     * of the viewer camera.
     *
     * @example
     * ```
     * viewer.getFieldOfView().then((fov) => { console.log(fov); });
     * ```
     */
    // FIXME: do we want this?
    //public getFieldOfView(): Promise<number> {
    //    return new Promise<number>((resolve: (value: number) => void, reject: (reason: Error) => void): void => {
    //        this._container.renderService.renderCamera$.pipe(first()).subscribe(
    //            (rc: RenderCamera): void => {
    //                resolve(rc.perspective.fov);
    //            },
    //            (error: Error): void => {
    //                reject(error);
    //            }
    //        );
    //    });
    //}
    /**
     * @ignore
     * Get the bearing of the current viewer camera.
     *
     * @description The bearing depends on how the camera
     * is currently rotated and does not correspond
     * to the compass angle of the current node if the view
     * has been panned.
     *
     * Bearing is measured in degrees clockwise with respect to
     * north.
     *
     * @returns {Promise<number>} Promise to the bearing
     * of the current viewer camera.
     *
     * @example
     * ```
     * viewer.getBearing().then((b) => { console.log(b); });
     * ```
     */
    // FIXME
    // public getBearing(): Promise<number> {
    //     return new Promise<number>((resolve: (value: number) => void, reject: (reason: Error) => void): void => {
    //         this._container.renderService.bearing$.pipe(first()).subscribe(
    //             (bearing: number): void => {
    //                 resolve(bearing);
    //             },
    //             (error: Error): void => {
    //                 reject(error);
    //             }
    //         );
    //     });
    // }
    /**
     * @ignore
     * Get the viewer's current point of view.
     *
     * @returns {Promise<IPointOfView>} Promise to the current point of view
     * of the viewer camera.
     *
     * @example
     * ```
     * viewer.getPointOfView().then((pov) => { console.log(pov); });
     * ```
     */
    // FIXME: do we want this?
    //public getPointOfView(): Promise<IPointOfView> {
    //    return new Promise<IPointOfView>(
    //        (resolve: (value: IPointOfView) => void, reject: (reason: Error) => void): void => {
    //            observableCombineLatest(
    //                this._container.renderService.renderCamera$,
    //                this._container.renderService.bearing$
    //            )
    //                .pipe(first())
    //                .subscribe(
    //                    ([rc, bearing]: [RenderCamera, number]): void => {
    //                        resolve({
    //                            bearing: bearing,
    //                            tilt: rc.getTilt(),
    //                        });
    //                    },
    //                    (error: Error): void => {
    //                        reject(error);
    //                    }
    //                );
    //        }
    //    );
    //}
    /**
     * @ignore
     * Get the viewer's current position
     *
     * @returns {Promise<ILatLon>} Promise to the viewers's current
     * position.
     *
     * @example
     * ```
     * viewer.getPosition().then((pos) => { console.log(pos); });
     * ```
     */
    // FIXME: do we want this?
    //public getPosition(): Promise<ILatLon> {
    //    return new Promise<ILatLon>((resolve: (value: ILatLon) => void, reject: (reason: Error) => void): void => {
    //        observableCombineLatest(
    //            this._container.renderService.renderCamera$,
    //            this._navigator.stateService.reference$
    //        )
    //            .pipe(first())
    //            .subscribe(
    //                ([render, reference]: [RenderCamera, ILatLonAlt]): void => {
    //                    resolve(this._observer.projection.cameraToLatLon(render, reference));
    //                },
    //                (error: Error): void => {
    //                    reject(error);
    //                }
    //            );
    //    });
    //}
    /**
     * Detect the viewer's new width and height and resize it.
     *
     * @description The components will also detect the viewer's
     * new size and resize their rendered elements if needed.
     *
     * @example
     * ```
     * viewer.resize();
     * ```
     */
    resize() {
        this._container.renderService.resize$.next(null);
    }
    /**
     * @ignore
     * Trigger the rendering of a single frame.
     *
     * @description Use this method with custom renderers to
     * force the viewer to rerender when the custom content
     * changes. Calling this multiple times before the next
     * frame is rendered will still result in only a single
     * frame being rendered.
     */
    // FIXME: Is this needed.
    //public triggerRerender(): void {
    //    this._container.glRenderer.triggerRerender();
    //}
    /**
     * Clean up and release all internal resources associated with
     * this viewer.
     *
     * @description This includes DOM elements, event bindings, and
     * WebGL resources.
     *
     * Use this method when you are done using the viewer and wish to
     * ensure that it no longer consumes browser resources. Afterwards,
     * you must not call any other methods on the viewer.
     *
     * @fires Viewer#removed
     *
     * @example
     * ```
     * viewer.remove();
     * ```
     */
    remove() {
        this._observer.dispose();
        this._componentController.remove();
        this._navigator.dispose();
        this._container.remove();
        this.fire(Viewer.removed, undefined, { type: Viewer.removed });
    }
    getNodesLookAtPosition(xyz, maxdist, epsg) {
        return new Promise((resolve, reject) => {
            this._navigator.stateService.currentNode$
                .pipe(first(), timeout({ each: 1, with: () => observableOf(null) }), mergeMap((currentnode) => this._navigator.lookAtService.lookAtView$(xyz, maxdist, epsg, currentnode)))
                .subscribe({
                next: (results) => {
                    const filtered_results = results.filter((result) => result.node.frame.streamtype != "calotte");
                    const imageinfos = filtered_results.map((result) => {
                        let sensor = result.mapinfo.sensorid;
                        if (result.node.frame.streamtype == "cubemap")
                            sensor = result.mapinfo.cubefaceindex;
                        return {
                            key: result.node.key,
                            stream: result.node.frame.stream,
                            framenumber: result.node.frame.framenumber,
                            sensor: sensor,
                            nx: result.mapinfo.coords.x,
                            ny: result.mapinfo.coords.y,
                            dataPath: result.node.frame.dataPath,
                        };
                    });
                    resolve(imageinfos);
                },
                error: (error) => {
                    reject(error);
                },
            });
        });
    }
    setCampaignLock(campaignLock) {
        this._campaignlock = campaignLock;
        const prev_filter = this._navigator.filterFrameProperties;
        // Remove all existing filters of frame.campaignID
        const filter = prev_filter.filter((item) => !(item == "frame.campaignID"));
        // If necessary, add new filter of frame.campaignID
        if (campaignLock) {
            filter.push("frame.campaignID");
        }
        this.setFrameFilter(filter);
    }
    get campaignlock() {
        return this._campaignlock;
    }
    /**
     * EPSG Getter
     *
     * Retrieves the EPSG (European Petroleum Survey Group) code representing the coordinate system used by the viewer.
     *
     * @returns {number} - The EPSG code of the viewer's current coordinate system.
     *
     * This getter provides access to the EPSG code, which specifies the coordinate system used by the viewer.
     * The EPSG code is a unique identifier that helps define the spatial reference system for geographic data.
     * It is used to accurately interpret and render geographical information within the viewer.
     */
    get epsg() {
        return this._epsg;
    }
    /**
     * Set Direction
     *
     * Sets the direction of the viewer's playback or navigation to the specified Direction (0 or 1).
     *
     * @param {EdgeDirection} direction - The direction to set for playback or navigation (0=forward, 1=backward).
     *
     * This method allows you to control the direction in which the viewer's playback or navigation operates.
     * You can specify the desired direction using the {@link direction} parameter, which should be one of the values
     * 0 (forward) or 1 (backward). The viewer will then adjust its behavior accordingly.
     */
    setDirection(direction) {
        this._navigator.playService.setDirection(direction);
    }
    /**
     * Set Speed
     *
     * Adjusts the playback or navigation speed of the viewer to the specified value.
     *
     * @param {number} speed - The speed at which playback or navigation should occur.
     *
     * This method allows you to control the speed at which the viewer performs playback or navigation.
     * You can specify the desired speed using the {@link speed} parameter, which should be a numerical value.
     * The viewer will then adjust its playback or navigation pace accordingly.
     */
    setSpeed(speed) {
        this._navigator.playService.setSpeed(speed);
    }
    /**
     * Play
     *
     * Initiates playback of viewer content.
     */
    play() {
        this._navigator.playService.play();
    }
    /**
     * Stop
     *
     * Temporarily suspends the playback or execution of viewer content, bringing it to a complete stop.
     */
    stop() {
        this._navigator.playService.stop();
    }
    /**
     * Playing Status Getter
     *
     * Retrieves the current playing status of the viewer's content.
     *
     * @returns {boolean} - A boolean value indicating whether the viewer's content is currently in a playing state.
     *
     * This getter provides access to the current playing status of the viewer's content.
     * It returns a boolean value, where 'true' indicates that content is actively playing, and 'false' indicates that it is not in a playing state.
     */
    get playing() {
        return this._navigator.playService.playing;
    }
    /**
     * Direction Getter
     *
     * Retrieves the current playback or navigation direction of the viewer's content.
     *
     * @returns {any} - The current direction of playback or navigation within the viewer's content.
     *
     * This getter allows you to access and retrieve the current playback or navigation direction of the viewer's content.
     * It returns the direction value, which may be represented using any data type, indicating the orientation or flow of
     * the content, such as forward, backward, or other directional indications.
     */
    get direction() {
        return this._navigator.playService.direction;
    }
    setAuthObject(authObject) {
        this.authObject = authObject;
        this._navigator.setAuthObject(authObject);
        this._container.scene.setAuthObject(authObject);
    }
    /**
     * Get Routes
     *
     * Retrieves routes and navigation information based on the specified options.
     *
     * @param {RoutesOptionsV2 | undefined | RoutesOptionsV3} options - Options defining the desired route and navigation parameters.
     * @returns {Promise<any>} - A Promise that resolves with the requested route and navigation information.
     *
     * This method enables you to retrieve routes and navigation data, including route parameters, waypoints,
     * and navigation settings. It communicates with the underlying API to fetch the requested information
     * and returns a Promise that resolves with the retrieved route and navigation details.
     */
    getRoutes(options) {
        return this._navigator.api.data.getRoutes(options);
    }
    getRouteLines(options) {
        if (this._navigator.api.data instanceof Infra3DDataProvider_v3) {
            return this._navigator.api.data.getRouteLines(options);
        }
        else {
            throw new Error("The function getRouteLines is only supported on migrated data.");
        }
    }
    getRouteHexes(options) {
        if (this._navigator.api.data instanceof Infra3DDataProvider_v3) {
            return this._navigator.api.data.getRouteHexes(options);
        }
        else {
            throw new Error("The function getRouteHexes is only supported on migrated data.");
        }
    }
    getAllCampaigns(bbox) {
        if (this._navigator.api.data instanceof Infra3DDataProvider_v3) {
            return this._navigator.api.data.getAllCampaigns(bbox);
        }
        else {
            throw new Error("The function getAllCampaigns is only supported on migrated data.");
        }
    }
    getCampaign(campaignUID) {
        if (this._navigator.api.data instanceof Infra3DDataProvider_v3) {
            return this._navigator.api.data.getCampaign(campaignUID);
        }
        else {
            throw new Error("The function getCampaign is only supported on migrated data.");
        }
    }
    /**
     * Returns the ToolHandler
     * @return {ToolHandler} returns the tool handler
     */
    getToolHandler() {
        return this._container.scene.getToolHandler();
    }
    /**
     * Prints names of measure tools in the scene to the console.
     */
    printMeasureToolNames() {
        // Retrieve measure tools from the scene's tool handler
        const tools = this.getScene().getToolHandler().getToolsByGroup("measure");
        // Extract names of the measure tools
        const toolNames = tools.map((tool) => tool.getName());
        // Print the names to the console or log them in a way that suits your application.
        console.log("Measure Tools:", toolNames.join(", "));
    }
    // // Function that processes all measuring tools
    // private _processAllMeasurements(results: any[]) {
    //     console.log("All Measurement Results:", results);
    // }
    /**
     * Attaches a callback to a measurement tool.
     * @param toolname - The name of the measurement tool to attach.
     * @param onmeasured - Callback function triggered when a measurement is finished.
     */
    attachToMeasurementTool(toolname, onmeasured) {
        // Retrieve measure tools from the scene's tool handler
        const measureTools = this.getScene().getToolHandler().getToolsByGroup("measure");
        // Check if the specified tool name is valid
        const isValidTool = measureTools.some((tool) => tool.getName() === toolname);
        if (!isValidTool) {
            console.error("Invalid Measure Tool:", toolname);
            return;
        }
        // Find the selected measurement tool
        const selectedTool = measureTools.find((tool) => tool.getName() === toolname);
        // Attach callback to the 'finished' event of the selected measurement tool
        selectedTool.on("finished", (info) => {
            // Process the measurement result
            const resultset = this._processMeasurementResult(toolname, info);
            // Trigger the provided callback with the measurement result
            onmeasured(resultset.shortResult, resultset.geometry);
            // Save or further process the result,
            // e.g. for overall processing after all measurements are completed
            // this._processAllMeasurements([resultset]);
        });
    }
    /**
     * Toggles the state (enabled/disabled) of a measurement tool.
     * @param toolname - The name of the measurement tool to toggle.
     */
    toggleMeasurementTool(toolname) {
        // Retrieve all measure tools from the scene's tool handler
        const measureTools = this.getScene().getToolHandler().getToolsByGroup("measure");
        // Check if the specified tool name is valid
        const isValidTool = measureTools.some((tool) => tool.getName() === toolname);
        if (!isValidTool) {
            // Log an error if the tool name is invalid
            console.error("Invalid Measure Tool:", toolname);
            return;
        }
        // Find the selected measurement tool
        const selectedTool = measureTools.find((tool) => tool.getName() === toolname);
        // Toggle the state of the selected measurement tool
        if (selectedTool.enabled) {
            // Disable the tool if it is currently enabled
            selectedTool.disable();
        }
        else {
            // Enable the tool if it is currently disabled
            selectedTool.enable();
        }
    }
    /**
     * Attaches a callback to the continuous draw event of a measurement tool.
     * @param toolname - The name of the measurement tool to attach.
     * @param onContinuousDraw - Callback function triggered during continuous draw.
     */
    attachToContinuousDraw(toolname, onContinuousDraw) {
        // Retrieve measure tools from the scene's tool handler
        const measureTools = this.getScene().getToolHandler().getToolsByGroup("measure");
        // Check if the specified tool name is valid
        const isValidTool = measureTools.some((tool) => tool.getName() === toolname);
        if (!isValidTool) {
            console.error("Invalid Measure Tool:", toolname);
            return;
        }
        // Find the selected measurement tool
        const selectedTool = measureTools.find((tool) => tool.getName() === toolname);
        // Attach callback to the 'continuousdraw' event of the selected measurement tool
        selectedTool.on("continousdraw", (continuousInfo) => {
            // Process the continuous draw result
            const resultset = this._processContinuousDraw(toolname, continuousInfo);
            // Trigger the provided callback with the continuous draw result
            onContinuousDraw(resultset.shortResult);
        });
    }
    orderLidarFragments(email, user_uid, fragments_uids) {
        return this._navigator.api.data.orderLidarFragments(email, user_uid, fragments_uids);
    }
    setUserInteraction(pan, zoom) {
        this.getScene().infrascene.userInputHandler.zoomEnabled = zoom;
        this.getScene().infrascene.userInputHandler.panEnabled = pan;
    }
    setCameraView(cameraView, smooth = true) {
        const streamtype = this.getScene().getCurrentGeoframe().getMeasureInfo().streamtype;
        if (cameraView.type === "pano" && (streamtype === STREAMTYPE.CALOTTE || streamtype === STREAMTYPE.CUBEMAP)) {
            this.getScene().infrascene.setCameraView(cameraView, smooth);
        }
        else if (cameraView.type === "panzoom" &&
            (streamtype === STREAMTYPE.MONO || streamtype === STREAMTYPE.STEREO)) {
            this.getScene().infrascene.setCameraView(cameraView, smooth);
        }
        else {
            throw new Error("Camera view type does not match the current stream type.");
        }
    }
    getCameraView() {
        return this.getScene().infrascene.getCameraView();
    }
}
/**
 * @ignore
 * Fired when the viewing direction of the camera changes.
 *
 * @description Related to the computed compass angle
 * ({@link Node.computedCA}) from SfM, not the original EXIF compass
 * angle.
 *
 * @event
 * @type {number} bearing - Value indicating the current bearing
 * measured in degrees clockwise with respect to north.
 */
Viewer.bearingchanged = "bearingchanged";
/**
 * @ignore
 * Fired when a pointing device (usually a mouse) is pressed and released at
 * the same point in the viewer.
 * @event
 * @type  {@link IViewerMouseEvent} event - Viewer mouse event data.
 */
Viewer.click = "click";
/**
 * @ignore
 * Fired when the right button of the mouse is clicked within the viewer.
 * @event
 * @type  {@link IViewerMouseEvent} event - Viewer mouse event data.
 */
Viewer.contextmenu = "contextmenu";
/**
 * @ignore
 * Fired when a pointing device (usually a mouse) is clicked twice at
 * the same point in the viewer.
 * @event
 * @type  {@link IViewerMouseEvent} event - Viewer mouse event data.
 */
Viewer.dblclick = "dblclick";
/**
 * @ignore
 * Fired when the viewer's vertical field of view changes.
 *
 * @event
 * @type  {@link IViewerEvent} event - The event object.
 */
Viewer.fovchanged = "fovchanged";
/**
 * @ignore
 * Fired when the viewer is loading more data.
 * @event
 * @type {boolean} loading - Boolean indicating whether the viewer is loading.
 */
Viewer.loadingchanged = "loadingchanged";
/**
 * @ignore
 * Fired when a pointing device (usually a mouse) is pressed within the viewer.
 * @event
 * @type  {@link IViewerMouseEvent} event - Viewer mouse event data.
 */
Viewer.mousedown = "mousedown";
/**
 * @ignore
 * Fired when a pointing device (usually a mouse) is moved within the viewer.
 * @description Will not fire when the mouse is actively used, e.g. for drag pan.
 * @event
 * @type  {@link IViewerMouseEvent} event - Viewer mouse event data.
 */
Viewer.mousemove = "mousemove";
/**
 * @ignore
 * Fired when a pointing device (usually a mouse) leaves the viewer's canvas.
 * @event
 * @type  {@link IViewerMouseEvent} event - Viewer mouse event data.
 */
Viewer.mouseout = "mouseout";
/**
 * @ignore
 * Fired when a pointing device (usually a mouse) is moved onto the viewer's canvas.
 * @event
 * @type  {@link IViewerMouseEvent} event - Viewer mouse event data.
 */
Viewer.mouseover = "mouseover";
/**
 * @ignore
 * Fired when a pointing device (usually a mouse) is released within the viewer.
 * @event
 * @type  {@link IViewerMouseEvent} event - Viewer mouse event data.
 */
Viewer.mouseup = "mouseup";
/**
 * @ignore
 * Fired when the viewer motion stops and it is in a fixed
 * position with a fixed point of view.
 * @event
 */
Viewer.moveend = "moveend";
/**
 * @ignore
 * Fired when the motion from one view to another start,
 * either by changing the position (e.g. when changing node) or
 * when changing point of view (e.g. by interaction such as pan and zoom).
 * @event
 */
Viewer.movestart = "movestart";
/**
 * Fired when the navigable state of the viewer changes.
 *
 * @description The navigable state indicates if the viewer supports
 * moving, i.e. calling the `moveToKey` and `moveDir`
 * methods. The viewer will not be in a navigable state if the cover
 * is activated and the viewer has been supplied a key. When the cover
 * is deactivated or activated without being supplied a key it will
 * be navigable.
 *
 * @event
 * @type {boolean} navigable - Boolean indicating whether the viewer is navigable.
 */
Viewer.navigablechanged = "navigablechanged";
/**
 * Fired every time the viewer navigates to a new node. It returns
 * a "Node" object representing the details of the newly selected node.
 *
 * @event
 * @type  {@link Node} node - Current node.
 */
Viewer.nodechanged = "nodechanged";
/**
 * @ignore
 * Fired when the viewer's position changes.
 *
 * @description The viewer's position changes when transitioning
 * between nodes.
 *
 * @event
 * @type  {@link IViewerEvent} event - The event object.
 */
Viewer.positionchanged = "positionchanged";
/**
 * @ignore
 * Fired when the viewer's point of view changes. The point of view changes
 * when the bearing, or tilt changes.
 *
 * @event
 * @type  {@link IViewerEvent} event - The event object.
 */
Viewer.povchanged = "povchanged";
/**
 * @ignore
 * Fired when the viewer is removed. After this event is emitted
 * you must not call any methods on the viewer.
 *
 * @event
 * @type  {@link IViewerEvent} event - The event object.
 */
Viewer.removed = "removed";
/**
 * @ignore
 * Fired every time the sequence edges of the current node changes.
 * @event
 * @type  {@link IEdgeStatus} status - The edge status object.
 */
Viewer.sequenceedgeschanged = "sequenceedgeschanged";
/**
 * @ignore
 * Fired every time the spatial edges of the current node changes.
 * @event
 * @type  {@link IEdgeStatus} status - The edge status object.
 */
Viewer.spatialedgeschanged = "spatialedgeschanged";
/**
 * Fired every time the camera configurations changes. It returns an array with
 * a number of "Node" objects, where each object represents a camera along with
 * its specifications.
 * @event
 * @type {string}
 */
Viewer.cameraconfigurationchanged = "cameraconfigurationchanged";
/**
 * Fired every time the campaign changes. It returns an array with details about
 * the campaigns, where each element in the array represents a "Campaign" object,
 * providing specific campaign specifications.
 * @event
 * @type {string}
 */
Viewer.campaignschanged = "campaignschanged";
/**
 * Fired every time the panoramic view rotates. It provides
 * details about the rotation, including the rotation changes
 * in "lat" (latitude), "lon" (longitude) and
 * "fov" (field of view).
 * @event
 * @type {string}
 */
Viewer.panorotationchanged = "panorotationchanged";
/**
 * Fired every time the pan and zoom in a perspective image changes.
 * It provides information about the "zoom" level, "panX"
 * (horizontal panning) and "panY" (vertical panning).
 * @event
 * @type {string}
 */
Viewer.panzoomchanged = "panzoomchanged";
/**
 * Fired every time the playing status of the player changes.
 * It returns a boolean value, where "true" indicates that the
 * player is currently playing.
 * @event
 * @type {string}
 */
Viewer.playingchanged = "playingchanged";
/**
 * Fired every time the player's direction changes, and it's
 * like a compass that's permanently stuck on "North" – always
 * returns "0" no matter which way you're headed.
 * @event
 * @type {string}
 */
Viewer.directionchanged = "directionchanged";
/**
 * Fired every time the speed of the player changes. It provides
 * a new speed value, which can range between 0 and 1.
 * @event
 * @type {string}
 */
Viewer.speedchanged = "speedchanged";
/**
 * Fired every time the collection (network) is changed.
 * @event
 * @type {string}
 */
Viewer.collectionchanged = "collectionchanged";
/**
 * Fired every time the current geoframe is changed. It returns a "GeoFrame"
 * object representing the updated geoframe. It contains various attributes
 * and information related to the geoframe, such as "campaign",
 * "capturesystem", "coordinates" and more. This object provides
 * comprehensive information about the geoframe and its associated properties
 * within the system.
 * @event
 * @type {string}
 */
Viewer.geoframechanged = "geoframechanged";
/**
 * Fired every time the geoframecollection is changed. It returns
 * a "GeoFrameCollection" object representing the updated geoframe
 * collection, which can include various properties and behaviors.
 * @event
 * @type {string}
 */
Viewer.geoframecollectionchanged = "geoframecollectionchanged";
/**
 * @ignore
 * Fired every time the navigation axis is changed.
 * @event
 * @type {string}
 */
Viewer.navigationaxischanged = "navigationaxischanged";
/**
 * @ignore
 * Fired every time the measurement axis is changed.
 * @event
 * @type {string}
 */
Viewer.measurementaxischanged = "measurementaxischanged";
/**
 * Fired every time the buffering is changed. It returns a boolean value
 * indicating whether buffering is currently "true" or "false".
 * @event
 * @type {string}
 */
Viewer.buffering = "buffering";
/**
 * @ignore
 * Fired every time the measuremode is changed.
 * @event
 * @type {string}
 */
Viewer.measuremodechanged = "measuremodechanged";
/**
 * Fired every time a layer is changed. Returns information about the modified layer.
 * @event
 * @type {string}
 */
Viewer.layerchanged = "layerchanged";
/**
 * Fired every time a layer is added. It returns a "Layer" object,
 * which can be of the type "Web Map Layer" like "WFS20" or local
 * as "Memory Layer", along with its associated properties.
 * @event
 * @type {string}
 */
Viewer.layeradded = "layeradded";
/**
 * Fired every time a layer is removed. Returns information about the removed layer.
 * @event
 * @type {string}
 */
Viewer.layerremoved = "layerremoved";
/**
 * Fired every time a layer is updated. It returns an array containing
 * the updated layer, which can be of the type "Web Map Layer" like "WFS20"
 * or local as "Memory Layer", along with additional information
 * about changes, such as added, updated, and removed features.
 * @event
 * @type {string}
 */
Viewer.layerupdated = "layerupdated";
/**
 * Fired every time a layer is saved. Returns information about the saved layer.
 * @event
 * @type {string}
 */
Viewer.layersaved = "layersaved";
/**
 * Fired every time a feature is selected. It returns an
 * array containing information about the selected feature.
 * @event
 * @type {string}
 */
Viewer.featureselected = "featureselected";
/**
 * Fired every time a feature is deselected.
 * @event
 * @type {string}
 */
Viewer.featuredeselected = "featuredeselected";
/**
 * Fired every time the azimuth, representing the direction you
 * are currently looking at, changes. It returns the new
 * azimuth value.
 * @event
 * @type {string}
 */
Viewer.lookazimuthchanged = "lookazimuthchanged";
//# sourceMappingURL=Viewer.js.map